import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface MusicContent {
  latest_release_title_en: string;
  latest_release_title_ro: string;
  latest_release_subtitle_en: string;
  latest_release_subtitle_ro: string;
  youtube_video_id: string;
  youtube_video_title: string;
  spotify_track_id: string;
  spotify_track_title: string;
  vlog_video_id: string;
  vlog_title_en: string;
  vlog_title_ro: string;
  vlog_subtitle: string;
  spotify_artist_url: string;
}

const MusicPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<MusicContent>('/api/content/music');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<MusicContent>({
    latest_release_title_en: '', latest_release_title_ro: '',
    latest_release_subtitle_en: '', latest_release_subtitle_ro: '',
    youtube_video_id: '', youtube_video_title: '',
    spotify_track_id: '', spotify_track_title: '',
    vlog_video_id: '', vlog_title_en: '', vlog_title_ro: '',
    vlog_subtitle: '', spotify_artist_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        latest_release_title_en: data.latest_release_title_en || '',
        latest_release_title_ro: data.latest_release_title_ro || '',
        latest_release_subtitle_en: data.latest_release_subtitle_en || '',
        latest_release_subtitle_ro: data.latest_release_subtitle_ro || '',
        youtube_video_id: data.youtube_video_id || '',
        youtube_video_title: data.youtube_video_title || '',
        spotify_track_id: data.spotify_track_id || '',
        spotify_track_title: data.spotify_track_title || '',
        vlog_video_id: data.vlog_video_id || '',
        vlog_title_en: data.vlog_title_en || '',
        vlog_title_ro: data.vlog_title_ro || '',
        vlog_subtitle: data.vlog_subtitle || '',
        spotify_artist_url: data.spotify_artist_url || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof MusicContent) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Music section saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Music Section"
        subtitle="Manage featured videos and Spotify embeds"
        actions={<Button onClick={handleSave} loading={saving}>Save Changes</Button>}
      />

      <div className="admin-content">
        <Card title="Section Header">
          <LanguageTabs currentLang={lang} onChange={setLang} />
          <div className="admin-form">
            {lang === 'en' ? (
              <>
                <Input label="Title (EN)" value={form.latest_release_title_en} onChange={handleChange('latest_release_title_en')} placeholder="Latest Release" />
                <Input label="Subtitle (EN)" value={form.latest_release_subtitle_en} onChange={handleChange('latest_release_subtitle_en')} placeholder="Stream my latest track..." />
              </>
            ) : (
              <>
                <Input label="Title (RO)" value={form.latest_release_title_ro} onChange={handleChange('latest_release_title_ro')} placeholder="Ultima lansare" />
                <Input label="Subtitle (RO)" value={form.latest_release_subtitle_ro} onChange={handleChange('latest_release_subtitle_ro')} placeholder="Ascultă cea mai recentă piesă..." />
              </>
            )}
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card title="Featured YouTube Video" subtitle="The main video shown at the top of the music section">
            <div className="admin-form">
              <Input
                label="YouTube Video ID"
                value={form.youtube_video_id}
                onChange={handleChange('youtube_video_id')}
                placeholder="TQqskjHzur4"
                hint="The ID from the YouTube URL (e.g., youtube.com/watch?v=TQqskjHzur4)"
              />
              <Input
                label="Video Title"
                value={form.youtube_video_title}
                onChange={handleChange('youtube_video_title')}
                placeholder="MONEY ON ME – Official Video"
              />
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Spotify Embed" subtitle="Featured track embed below the video">
            <div className="admin-form">
              <Input
                label="Spotify Track ID"
                value={form.spotify_track_id}
                onChange={handleChange('spotify_track_id')}
                placeholder="7FdGtHsFVF3PUlWs4mGFAq"
                hint="The track ID from Spotify (open.spotify.com/track/[ID])"
              />
              <Input
                label="Track Title"
                value={form.spotify_track_title}
                onChange={handleChange('spotify_track_title')}
                placeholder="MONEY ON ME"
              />
              <Input
                label="Spotify Artist URL"
                value={form.spotify_artist_url}
                onChange={handleChange('spotify_artist_url')}
                placeholder="https://open.spotify.com/artist/..."
                hint="Full URL to your Spotify artist page"
              />
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Vlog Section" subtitle="Optional vlog video shown below the music">
            <LanguageTabs currentLang={lang} onChange={setLang} />
            <div className="admin-form">
              <Input
                label="YouTube Vlog Video ID"
                value={form.vlog_video_id}
                onChange={handleChange('vlog_video_id')}
                placeholder="vZ_fGaPhbJ4"
              />
              {lang === 'en' ? (
                <Input label="Vlog Title (EN)" value={form.vlog_title_en} onChange={handleChange('vlog_title_en')} placeholder="Latest Vlog" />
              ) : (
                <Input label="Vlog Title (RO)" value={form.vlog_title_ro} onChange={handleChange('vlog_title_ro')} placeholder="Cel mai recent vlog" />
              )}
              <Input
                label="Vlog Subtitle"
                value={form.vlog_subtitle}
                onChange={handleChange('vlog_subtitle')}
                placeholder="Beach Please Festival 25"
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default MusicPage;
